// Libraries
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getAccessToken, getUser, putUser } from './userAPI';

// Store
import { RootState } from '../../app/store';

// Models
import { User } from './userModels';

/**
 * @interface UserState
 * Describes the properties for the global settings state
 *
 * @property {User} user The current user
 * @property {string} status Current data fetching status
 */
export interface UserState {
    user: User;
    status: 'idle' | 'loading' | 'failed';
}

/**
 * @const initialState
 * Describes the initial settings state on application start up
 */
const initialState: UserState = {
    user: {
        id: 0,
        username: '',
        avatar: `https://${process.env.REACT_APP_CDN}/play-bingo/images/avatars/default.jpg`,
        points: 0,
        game_mods: [],
        chat_on: true,
        flashcard_on: true,
        heartbeat_on: true,
        sound_on: true,
        dauber_color: '',
        dauber_icon: '',
        voice: '',
        blocked: [],
        external_id: 0,
        external_member_level: 0,
        external_campaign_id: 0,
        external_tracking_id: 0,
        region: '',
        current_session: null,
        active_access_token_count: 0,
    },
    status: 'idle',
};

/**
 * @function getAccessTokenAsync
 * Attempts to purchase an access token
 * https://redux-toolkit.js.org/api/createAsyncThunk
 *
 * @return {object} Object containing user's updated settings
 */
export const getAccessTokenAsync = createAsyncThunk(
    'user/getAccessToken',
    async () => {
        const response = await getAccessToken();
        return response;
    },
);

/**
 * @function getUserAsync
 * Gets current user
 * https://redux-toolkit.js.org/api/createAsyncThunk
 *
 * @param {number} userId Target User ID
 * @return {object} Object containing user's updated settings
 */
export const getUserAsync = createAsyncThunk(
    'user/getUser',
    async (userId: number) => {
        const response = await getUser(userId);

        response.id = userId;
        return response;
    },
);

/**
 * @function putUserAsync
 * Pushes current user's updated settings
 * https://redux-toolkit.js.org/api/createAsyncThunk
 *
 * @param {number} userId The current user's ID to authenticate
 * @param {Settings} settings The user's updated prefereces
 *
 * @return {object} Object containing user's updated settings
 */
export const putUserAsync = createAsyncThunk(
    'user/postSettings',
    async ({ user }: { user: User }) => {
        const response = await putUser(user.id, {
            sound_on: user.sound_on,
            chat_on: user.chat_on,
            flashcard_on: user.flashcard_on,
            heartbeat_on: user.heartbeat_on,
            dauber_color: user.dauber_color,
            dauber_icon: user.dauber_icon,
            voice: user.voice,
            blocked: user.blocked,
        });

        response.id = user.id;
        return response;
    },
);

/**
 * @const usersSlice
 * Describes initial state and reducer functions to handle user state changes
 * https://redux-toolkit.js.org/api/createslice
 */
export const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        pushUser: (state, action) => {
            state.user = { ...state.user, ...action.payload };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getAccessTokenAsync.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(getAccessTokenAsync.fulfilled, (state, action) => {
                state.status = 'idle';
                console.log('Access token purchased');
                state.user = { ...state.user, ...action.payload };
            })
            .addCase(getAccessTokenAsync.rejected, (state) => {
                state.status = 'failed';
            })
            .addCase(getUserAsync.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(getUserAsync.fulfilled, (state, action) => {
                state.status = 'idle';
                state.user = { ...state.user, ...action.payload };
            })
            .addCase(getUserAsync.rejected, (state) => {
                state.status = 'failed';
            })
            .addCase(putUserAsync.pending, (state) => {
                state.status = 'loading';
            })
            .addCase(putUserAsync.fulfilled, (state, action) => {
                state.status = 'idle';
                state.user = { ...state.user, ...action.payload };
            })
            .addCase(putUserAsync.rejected, (state) => {
                state.status = 'failed';
            });
    },
});

/**
 * @const selectUser
 * Exposes user property in user state for use in rest of application
 */
export const selectUser = (state: RootState) => state.user.user;

/**
 * @const pushUser
 * Exposes pushUser reducer for use in rest of application
 */
export const { pushUser } = userSlice.actions;

/**
 * @const userSlice.reducer
 * Exposes user reducer for use in store
 */
export default userSlice.reducer;
